import React, { useEffect, useState, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import BodyMap from "../components/readiness/BodyMap";
import { ZONE_LABELS, LEVEL_COLOR } from "../components/readiness/bodyMapConfig";
import { ACADEMY_LOGO_H } from "../data/assets";
import { Activity, AlertTriangle, Users, RefreshCw, Flame, LogOut, GraduationCap } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const BACKEND = process.env.REACT_APP_BACKEND_URL;
const tone = (s) => (s == null ? "#6b7280" : s >= 85 ? "#22C55E" : s >= 60 ? "#EAB308" : s >= 40 ? "#F97316" : "#EF4444");

function BigRing({ score }) {
  const r = 52, c = 2 * Math.PI * r, t = tone(score);
  return (
    <div className="relative w-36 h-36" data-testid="inst-team-index">
      <svg viewBox="0 0 120 120" className="w-36 h-36 -rotate-90">
        <circle cx="60" cy="60" r={r} fill="none" stroke="rgba(255,255,255,0.08)" strokeWidth="10" />
        <circle cx="60" cy="60" r={r} fill="none" stroke={t} strokeWidth="10" strokeLinecap="round"
          strokeDasharray={c} strokeDashoffset={score == null ? c : c - (c * score) / 100}
          style={{ transition: "stroke-dashoffset 0.7s ease" }} />
      </svg>
      <div className="absolute inset-0 flex flex-col items-center justify-center">
        <span className="font-cover text-4xl leading-none" style={{ color: t }}>{score == null ? "—" : `${score}%`}</span>
        <span className="text-[9px] uppercase tracking-widest text-white/50 mt-1">Team Ready</span>
      </div>
    </div>
  );
}

function RedFlag({ r }) {
  return (
    <div data-testid={`inst-redflag-${r.player_id}`} className="rounded-lg border p-3"
      style={{ borderColor: r.worst_level === "red" ? "#EF444455" : "#F9731655", background: r.worst_level === "red" ? "#EF44440d" : "#F973160d" }}>
      <div className="flex items-center gap-3">
        {r.avatar_url ? <img loading="lazy" decoding="async" src={`${BACKEND}${r.avatar_url}`} alt="" className="w-9 h-9 rounded-full object-cover" />
          : <div className="w-9 h-9 rounded-full bg-white/10 flex items-center justify-center text-xs font-bold text-white/60">{(r.name || "P")[0]}</div>}
        <div className="min-w-0">
          <div className="text-sm font-bold text-white truncate">{r.name}{r.jersey_number ? ` · #${r.jersey_number}` : ""}</div>
          <div className="text-[11px] text-white/45">{r.team || "No team"}</div>
        </div>
        <div className="ml-auto text-right">
          <div className="font-cover text-2xl leading-none" style={{ color: tone(r.score) }}>{r.score}%</div>
          <div className="text-[9px] uppercase tracking-widest text-white/40">ready</div>
        </div>
      </div>
      <div className="flex flex-wrap gap-1.5 mt-2.5">
        {r.areas.map((a, i) => (
          <span key={i} className="text-[10px] font-bold uppercase tracking-wide rounded-full px-2 py-0.5"
            style={{ background: `${LEVEL_COLOR[a.level]}22`, color: LEVEL_COLOR[a.level], border: `1px solid ${LEVEL_COLOR[a.level]}55` }}>
            {ZONE_LABELS[a.part]}
          </span>
        ))}
      </div>
      {r.recovery_log && <p className="text-[11px] text-white/50 mt-2 italic line-clamp-2">"{r.recovery_log}"</p>}
    </div>
  );
}

export default function InstitutionDashboard() {
  const navigate = useNavigate();
  const [token] = useState(localStorage.getItem("clubCoachToken") || "");
  const [info] = useState(() => { try { return JSON.parse(localStorage.getItem("clubCoachInfo") || "{}"); } catch { return {}; } });
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [view, setView] = useState("front");

  useEffect(() => {
    if (!token) { navigate("/login", { replace: true }); return; }
    if (!info.school_id) { navigate("/club-dashboard", { replace: true }); }
  }, [token, info, navigate]);

  const load = useCallback(() => {
    if (!token) return;
    setLoading(true);
    axios.get(`${API}/club-coach/readiness`, { headers: { Authorization: `Bearer ${token}` } })
      .then(({ data }) => setData(data))
      .catch((e) => {
        if (e?.response?.status === 401 || e?.response?.status === 403) {
          toast.error("Session expired. Please sign in again.");
          localStorage.removeItem("clubCoachToken"); navigate("/login", { replace: true });
        } else toast.error("Couldn't load readiness data.");
      })
      .finally(() => setLoading(false));
  }, [token, navigate]);
  useEffect(() => { load(); }, [load]);

  const logout = () => {
    localStorage.removeItem("clubCoachToken");
    localStorage.removeItem("clubCoachInfo");
    localStorage.removeItem("nexto_remember");
    navigate("/login", { replace: true });
  };

  const heat = data?.heatmap || {};

  return (
    <div className="min-h-screen bg-[#0a0a0a] text-white">
      <header className="sticky top-0 z-30 bg-[#0a0a0a]/85 backdrop-blur-xl border-b border-white/10 px-4 sm:px-6 pb-4 header-safe flex items-center justify-between gap-3">
        <div className="flex items-center gap-3 min-w-0">
          <img src={ACADEMY_LOGO_H} alt="The Next O Technical Academy" className="h-12 w-auto object-contain shrink-0" />
          <div className="min-w-0">
            <div className="text-[10px] uppercase tracking-widest text-[#D4AF37] font-bold flex items-center gap-1"><GraduationCap size={12} /> Institution</div>
            <div className="font-headings text-base uppercase tracking-wide truncate">{data?.school_name || info.school_name || "Readiness"}</div>
          </div>
        </div>
        <div className="flex items-center gap-2 shrink-0">
          <button data-testid="inst-refresh" onClick={load} className="p-2 text-white/50 hover:text-white border border-white/15 rounded-md" title="Refresh"><RefreshCw size={15} className={loading ? "animate-spin" : ""} /></button>
          <button data-testid="inst-logout" onClick={logout} className="text-white/50 hover:text-white p-2" aria-label="Log out"><LogOut size={16} /></button>
        </div>
      </header>

      <div className="max-w-5xl mx-auto px-4 sm:px-6 py-8" data-testid="institution-dashboard">
        <div className="flex items-center gap-3 mb-1">
          <Activity size={18} className="text-[#D4AF37]" />
          <div>
            <div className="text-[10px] font-bold uppercase tracking-[0.28em] text-white/40">Readiness Heatmap</div>
            <h1 className="font-headings text-2xl sm:text-3xl uppercase tracking-wide leading-none">Team Readiness</h1>
          </div>
        </div>
        <p className="text-sm text-white/45 mb-6">Today's live readiness across your athletes — spot strain and red flags before they become injuries.</p>

        <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 mb-6">
          <div className="bg-[#141414] border rounded-xl p-5 flex items-center gap-5" style={{ borderColor: `${tone(data?.team_readiness)}44` }}>
            <BigRing score={data?.team_readiness ?? null} />
            <div>
              <div className="text-[10px] font-bold uppercase tracking-widest text-white/45 mb-1">Readiness Index</div>
              <div className="text-sm text-white/60">{data?.school_name || "Your squad"}</div>
              <div className="text-xs text-white/40 mt-2">{data?.date}</div>
            </div>
          </div>
          <div className="bg-[#141414] border border-white/10 rounded-xl p-5">
            <div className="flex items-center justify-between mb-2"><span className="text-[10px] font-bold uppercase tracking-widest text-white/45">Checked In Today</span><Users size={16} className="text-[#D4AF37]" /></div>
            <div className="font-cover text-5xl text-white leading-none" data-testid="inst-checked-in">{data?.checked_in ?? 0}<span className="text-2xl text-white/30"> / {data?.roster ?? 0}</span></div>
            <div className="text-xs text-white/45 mt-2">athletes reported</div>
          </div>
          <div className="bg-[#141414] border rounded-xl p-5" style={{ borderColor: (data?.red_flags?.length ? "#EF444455" : "rgba(255,255,255,0.1)") }}>
            <div className="flex items-center justify-between mb-2"><span className="text-[10px] font-bold uppercase tracking-widest text-white/45">Red Flags</span><AlertTriangle size={16} className={data?.red_flags?.length ? "text-[#EF4444]" : "text-white/40"} /></div>
            <div className="font-cover text-5xl leading-none" style={{ color: data?.red_flags?.length ? "#EF4444" : "#fff" }} data-testid="inst-redflag-count">{data?.red_flags?.length ?? 0}</div>
            <div className="text-xs text-white/45 mt-2">medium/high pain{data?.cleared ? ` · ${data.cleared} cleared` : ""}</div>
          </div>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          <div className="bg-[#141414] border border-white/10 rounded-xl p-6" data-testid="inst-heatmap">
            <div className="flex items-center gap-2 mb-4"><Flame size={16} className="text-[#F97316]" /><h3 className="font-subheadings text-sm font-bold uppercase tracking-widest text-white/80">Team Strain Heatmap</h3></div>
            <BodyMap mode="heat" heat={heat} accent="#F97316" view={view} onViewChange={setView} />
            <div className="grid grid-cols-2 gap-x-4 gap-y-1.5 mt-5">
              {Object.entries(heat).filter(([, c]) => (c.yellow + c.orange + c.red) > 0)
                .sort((a, b) => b[1].strain - a[1].strain)
                .map(([part, c]) => (
                  <div key={part} className="flex items-center justify-between text-xs">
                    <span className="text-white/70">{ZONE_LABELS[part]}</span>
                    <span className="flex items-center gap-1.5">
                      {c.red > 0 && <span className="px-1.5 py-0.5 rounded text-[10px] font-bold" style={{ background: "#EF444422", color: "#EF4444" }}>{c.red}</span>}
                      {c.orange > 0 && <span className="px-1.5 py-0.5 rounded text-[10px] font-bold" style={{ background: "#F9731622", color: "#F97316" }}>{c.orange}</span>}
                      {c.yellow > 0 && <span className="px-1.5 py-0.5 rounded text-[10px] font-bold" style={{ background: "#EAB30822", color: "#EAB308" }}>{c.yellow}</span>}
                    </span>
                  </div>
                ))}
              {Object.values(heat).every((c) => (c.yellow + c.orange + c.red) === 0) && (
                <div className="col-span-2 text-center text-sm text-[#22C55E] py-4">No strain reported today — squad is fresh. 💪</div>
              )}
            </div>
          </div>

          <div className="bg-[#141414] border border-white/10 rounded-xl p-6" data-testid="inst-redflags">
            <div className="flex items-center gap-2 mb-4"><AlertTriangle size={16} className="text-[#EF4444]" /><h3 className="font-subheadings text-sm font-bold uppercase tracking-widest text-white/80">Red-Flag Alerts</h3></div>
            {(!data?.red_flags || data.red_flags.length === 0) ? (
              <p className="text-center text-white/40 text-sm py-10">No red flags today. Every reporting athlete is good to train.{data?.cleared ? ` (${data.cleared} cleared)` : ""}</p>
            ) : (
              <div className="space-y-3">{data.red_flags.map((r) => <RedFlag key={r.player_id} r={r} />)}</div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
